import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Input, cn } from '@mlm/ui';
import type { AssistantConversationDto, AssistantPersonaDto } from '../../lib/assistant-api';
import { PersonaAvatar } from './persona-avatar';

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

/** Thread sidebar (desktop) / home list (mobile). */
export function ConversationList({
  conversations,
  personas,
  selectedId,
  onSelect,
  onNew,
  onDelete,
  onRename,
}: {
  conversations: AssistantConversationDto[];
  personas: AssistantPersonaDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
}) {
  const personaFor = (personaId: string) => personas.find((p) => p.id === personaId) ?? null;
  // Thread being renamed inline, with its in-progress title.
  const [editing, setEditing] = useState<{ id: string; title: string } | null>(null);

  function commitRename() {
    if (!editing) return;
    const title = editing.title.trim().slice(0, 80);
    const previous = conversations.find((c) => c.id === editing.id)?.title ?? null;
    if (title && title !== previous) onRename(editing.id, title);
    setEditing(null);
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <Button onClick={onNew} variant="outline" className="w-full justify-start gap-2">
        <Plus className="h-4 w-4" /> New chat
      </Button>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
        {conversations.length === 0 && (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">
            No chats yet — start one!
          </p>
        )}
        {conversations.map((c) => (
          <div
            key={c.id}
            className={cn(
              'group flex items-center gap-2 rounded-md px-2 py-2 transition-colors',
              selectedId === c.id ? 'bg-o-l/50' : 'hover:bg-muted/60',
            )}
          >
            {editing?.id === c.id ? (
              <Input
                autoFocus
                value={editing.title}
                maxLength={80}
                aria-label="Chat name"
                className="h-8 flex-1 text-sm"
                onChange={(e) => setEditing({ id: c.id, title: e.target.value })}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') setEditing(null);
                }}
              />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onSelect(c.id)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <PersonaAvatar
                    icon={personaFor(c.personaId)?.icon ?? '✨'}
                    avatarKey={personaFor(c.personaId)?.avatarKey}
                    name={personaFor(c.personaId)?.name}
                    size="sm"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{c.title ?? 'New chat'}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {relativeTime(c.lastMessageAt ?? c.createdAt)}
                    </span>
                  </span>
                </button>
                {/* Visible by default on touch; hover-reveal only where hover exists. */}
                <button
                  type="button"
                  aria-label="Rename chat"
                  className="flex-none rounded text-muted-foreground opacity-60 transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:opacity-0 lg:group-hover:opacity-100"
                  onClick={() => setEditing({ id: c.id, title: c.title ?? '' })}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Delete chat"
                  className="flex-none rounded text-muted-foreground opacity-60 transition-opacity hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:opacity-0 lg:group-hover:opacity-100"
                  onClick={() => onDelete(c.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
